/**
 * CustomEditorBuild.js
 *
 * Uses the unified `ckeditor5` super-package (v48.3.1) already in your
 * package.json -- CKEditor5's v42+ installation method.
 *
 * MATH/CHEMISTRY: `@isaul32/ckeditor5-math` was tried and does NOT work
 * with your ckeditor5 v48 -- confirmed by real errors: it bundles its own
 * nested @ckeditor/ckeditor5-core (causing duplicated-module SVG build
 * failures), doesn't export what its docs claim, and ships no dist CSS at
 * the documented path. It has been fully removed.
 *
 * Instead, math/chemistry equations are handled by `MathEquation`, a small
 * plugin built for this project (see ../plugins/mathequation/), using only
 * your existing `ckeditor5` package + `katex` for rendering. No third-party
 * CKEditor plugin, so no version-mismatch risk.
 *
 * Install (only 1 new package -- everything else already in package.json):
 *   npm uninstall @isaul32/ckeditor5-math ckeditor5-math
 *   npm install katex
 */

import { ClassicEditor, Essentials, Heading, Link, List, Table, TableToolbar, BlockQuote,
  Alignment, FontColor, FontBackgroundColor, FontSize, Indent, IndentBlock, RemoveFormat,
  HorizontalLine, SourceEditing, Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
  GeneralHtmlSupport,
} from 'ckeditor5';
import 'ckeditor5/ckeditor5.css';
import 'katex/dist/katex.min.css';

// Registers the \ce{...} macro with KaTeX so chemistry notation renders.
// Without this import, \ce{2H2 + O2 -> 2H2O} will show a KaTeX parse error.
import 'katex/contrib/mhchem/mhchem.js';

// Self-built math/chemistry equation widget (see file header above).
import MathEquation from './mathequation';

// "Math/Chem Equation" toolbar button: free-type LaTeX input + Wiris-style
// clickable template palette + resizable NxN matrix builder. Also handles
// click-to-edit on existing equations.
import TemplatePanel from './TemplatePanel';

// Explicitly registers <math> with GHS as inline content -- see file for
// why the htmlSupport.allow config below may not be sufficient alone.
import LegacyMathMLSupport from './LegacyMathRenderer';

export default class CustomEditor extends ClassicEditor {}

CustomEditor.builtinPlugins = [
  Essentials,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Subscript,
  Superscript,
  RemoveFormat,
  Heading,
  Link,
  List,
  Table,
  TableToolbar,
  BlockQuote,
  HorizontalLine,
  Alignment,
  FontColor,
  FontBackgroundColor,
  FontSize,
  Indent,
  IndentBlock,
  SourceEditing,
  GeneralHtmlSupport, // lets old Wiris <math> MathML pass through untouched -- see htmlSupport config below
  LegacyMathMLSupport,
  MathEquation,
  TemplatePanel,
];

CustomEditor.defaultConfig = {
  // REQUIRED since CKEditor5 v44 -- without this, editor.create() rejects
  // and the editor never renders, with little/no visible error. 'GPL' is
  // correct for self-hosted (npm) use under the GPL 2+ license. If your
  // organization has a commercial CKEditor license, replace 'GPL' with
  // your actual license key from the CKEditor Customer Portal instead.
  licenseKey: 'GPL',
  toolbar: {
    shouldNotGroupWhenFull: true,
    items: [
      'heading',
      'fontsize',
      'alignment',
      'fontColor',
      'fontBackgroundColor',
      'outdent',
      'indent',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'sourceEditing',
      '|',
      'superscript',
      'subscript',
      'removeFormat',
      'link',
      'bulletedList',
      'numberedList',
      'insertTable',
      'blockQuote',
      'horizontalLine',
      '|',
      'templatePanel', // single "Math/Chem Equation" button -- free-type + templates + matrix builder
      '|',
      'undo',
      'redo',
    ],
  },
  table: {
    contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'],
  },
  language: 'en',
  // Lets old Wiris/MathML content from your 5 lakh+ existing questions
  // pass through completely untouched instead of being stripped down to
  // bare text (which is what happened before this was added -- e.g. a
  // fraction like 4/3 lost its <mfrac> structure and collapsed into "43").
  // This does NOT make old equations editable/re-insertable as the new
  // KaTeX widget -- it just stops CKEditor from destroying them. Modern
  // browsers (Chrome, Firefox, Safari) render <math> natively, so old
  // content just displays correctly again, unchanged, with no lossy
  // conversion. New equations keep using the separate KaTeX-based
  // mathEquation widget from ../plugins/mathequation/.
  htmlSupport: {
    allow: [
      {
        name: /^(math|mi|mo|mn|mfrac|msup|msub|msubsup|mrow|msqrt|mroot|mtable|mtr|mtd|mspace|mtext|mstyle|mfenced|mover|munder|munderover|semantics|annotation|mpadded|menclose|mmultiscripts|mlabeledtr|maligngroup|malignmark)$/,
        attributes: true,
        classes: true,
        styles: true,
      },
    ],
  },
};